/**
 * 구성원 등록 / 수정
 * ---------------------------------------------------------------------------
 * 서버 제약을 화면에서 미리 알려 주는 것이 이 폼의 역할이다.
 *
 *   - 이메일은 등록 후 변경할 수 없다(계정 식별자이며 암호화 인덱스가 걸려 있다)
 *   - 신규 등록은 초대 메일로 진행된다. 비밀번호를 관리자가 정하지 않는다
 *   - 생년월일은 월일만 평문으로 저장된다(생일 알림용)
 */

import { Alert, DatePicker, Divider, Form, Input, Modal, Select, Space, Typography } from 'antd';
import { useState } from 'react';
import dayjs from 'dayjs';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import {
  askSeatApproval,
  isSeatApprovalRequired,
  isSeatLimitExceeded,
  showSeatLimit,
} from './seatOverage';
import {
  errorText,
  flattenDepartments,
  useApiMutation,
  useCodes,
  useDepartments,
  useInvalidateUsers,
  useRoles,
} from '../../lib/queries';

const { Text } = Typography;

/**
 * 폼을 세 묶음으로 나누는 제목.
 *
 * <b>왜 탭이 아닌가</b>
 *   탭으로 나누면 다른 탭의 입력 오류가 보이지 않는다. "저장이 안 되는데
 *   왜인지 모르겠다" 가 되고, 사람은 보이는 탭만 들여다본다. 한 화면에
 *   세로로 두고 제목으로만 끊는다.
 *
 * <b>왜 나누나</b>
 *   칸이 열 개를 넘으면 평평한 목록은 읽히지 않는다. 묶어 두면 "지금
 *   뭘 적는 중인지" 가 보이고, 인사 정보만 고치러 들어온 사람이 역할
 *   칸을 건드릴 일도 줄어든다.
 */
function Section({ no, title, hint }: { no: number; title: string; hint?: string }) {
  return (
    <div style={{ margin: '4px 0 12px' }}>
      <Space align="center" size={8}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: '#1677ff',
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {no}
        </span>
        <Text strong style={{ fontSize: 14 }}>
          {title}
        </Text>
      </Space>
      {hint && (
        <div style={{ marginTop: 2, marginLeft: 28 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {hint}
          </Text>
        </div>
      )}
    </div>
  );
}

export interface UserFormValue {
  userId?: string;
  email: string;
  name: string;
  phone?: string | null;
  birthDate?: string | null;
  departmentId?: string | null;
  empNo?: string | null;
  positionCode?: string | null;
  employmentType?: string;
  hireDate?: string | null;
  /** 이 사람이 가진 플랫폼 역할 */
  roleIds?: string[];
  /** 제품 안에서의 역할. 예: { crm: 'TeamLeader' } */
  productRoles?: Record<string, string>;
}

/**
 * WORK&CRM 의 역할.
 *
 * CRM 이 쓰는 이름을 그대로 적는다(AspNetRoles 의 Name). 플랫폼은 이 값의
 * 뜻을 모르고 적어 두기만 하며, CRM 이 동기화할 때 읽어 해석한다.
 *
 * 목록을 여기 둔 이유 — 제품의 역할 체계를 플랫폼이 실시간으로 물어볼 통로가
 * 아직 없다. 제품이 늘어나면 제품별 역할 목록을 내려주는 API 로 옮긴다.
 */
const CRM_ROLES = [
  { value: 'Admin', label: '시스템관리자 (Admin)' },
  { value: 'CEO', label: '대표이사 (CEO)' },
  { value: 'DepartmentHead', label: '부서장 (DepartmentHead)' },
  { value: 'TeamLeader', label: '팀장 (TeamLeader)' },
  { value: 'TeamMember', label: '팀원 (TeamMember)' },
];

interface Props {
  open: boolean;
  /** 수정 대상. 없으면 신규 등록 */
  target?: UserFormValue | null;
  onClose: () => void;
}

/**
 * 사용자가 「아니오」를 골라 멈춘 것.
 *
 * 저장을 중단해야 하지만 **오류가 아니다.** 그냥 throw 하면 폼 위에 빨간
 * 문구가 뜨는데, 사용자는 틀린 것이 아니라 그만두기를 고른 것이다.
 * 메시지를 비워 두고, 폼이 빈 메시지는 그리지 않는다.
 */
class SilentAbort extends Error implements NormalizedError {
  readonly code = 'ABORTED';
  constructor() {
    super('');
  }
}

const EMPLOYMENT_FALLBACK = [
  { itemCode: 'REGULAR', itemName: '정규직' },
  { itemCode: 'CONTRACT', itemName: '계약직' },
  { itemCode: 'PARTTIME', itemName: '시간제' },
  { itemCode: 'INTERN', itemName: '인턴' },
  { itemCode: 'DISPATCH', itemName: '파견' },
  { itemCode: 'EXTERNAL', itemName: '외부협력' },
];

export default function UserFormModal({ open, target, onClose }: Props) {
  const [form] = Form.useForm();
  const isEdit = Boolean(target?.userId);

  /** 상세를 받아 오는 동안 저장을 막는다. 받기 전에 저장하면 빈 값으로 덮어쓴다. */
  const [loadingDetail, setLoadingDetail] = useState(false);

  const codes = useCodes(['POSITION', 'EMPLOYMENT_TYPE']);
  const departments = useDepartments();
  const roles = useRoles();
  const invalidate = useInvalidateUsers();

  const save = useApiMutation<UserFormValue>(
    async (values) => {
      // 날짜는 서버가 YYYY-MM-DD 문자열을 기대한다.
      const payload = {
        name: values.name,
        phone: values.phone || undefined,
        birthDate: values.birthDate || undefined,
        departmentId: values.departmentId || undefined,
        empNo: values.empNo || undefined,
        positionCode: values.positionCode || undefined,
        employmentType: values.employmentType ?? 'REGULAR',
        hireDate: values.hireDate || undefined,
        roleIds: values.roleIds ?? [],
        /*
         * 빈 문자열도 그대로 보낸다 — 서버가 "지정 없음" 으로 보고 기존
         * 지정을 지운다. undefined 로 빼면 예전 값이 남는다.
         */
        productRoles: { crm: values.productRoles?.crm ?? '' },
      };

      if (isEdit) {
        await apiClient.patch(`/users/${target!.userId}`, payload);
        return;
      }

      /*
       * 신규 등록은 계약 인원에 걸릴 수 있다.
       *
       * 서버는 거절하면서 **얼마가 늘어나는지**를 함께 준다. 그 금액을 띄우고
       * 「예」를 받으면 사유와 함께 다시 보낸다. 사유가 있으면 서버가 계약
       * 수량을 늘리고 근거를 남긴다.
       *
       * 「아니오」면 조용히 멈춘다 — 오류로 처리하면 빨간 문구가 뜨는데,
       * 사용자는 틀린 것이 아니라 **그만두기를 고른** 것이다.
       */
      const post = (seatOverageReason?: string) =>
        apiClient.post('/users', {
          ...payload,
          // 이메일은 신규 등록에서만 보낸다. 수정 API 는 이메일을 받지 않는다.
          email: values.email,
          ...(seatOverageReason ? { seatOverageReason } : {}),
        });

      try {
        await post();
      } catch (err) {
        const error = err as NormalizedError;

        if (isSeatLimitExceeded(error)) {
          showSeatLimit(error);
          throw new SilentAbort();
        }

        if (!isSeatApprovalRequired(error)) throw err;

        const reason = await askSeatApproval(error);
        if (!reason) throw new SilentAbort();

        await post(reason);
      }
    },
    {
      onSuccess: () => {
        invalidate();
        onClose();
      },
    },
  );

  /**
   * 폼 초기화는 모달이 실제로 열린 뒤에 한다.
   *
   * destroyOnHidden 이라 닫혀 있는 동안에는 <Form> 이 DOM 에 없다.
   * 그 상태에서 form 인스턴스를 만지면 antd 가
   * "useForm is not connected to any Form element" 경고를 낸다.
   */
  const applyInitialValues = (opened: boolean) => {
    if (!opened) return;
    form.resetFields();
    if (!target) return;

    const fill = (value: UserFormValue) =>
      form.setFieldsValue({
        ...value,
        birthDate: value.birthDate ? dayjs(value.birthDate) : undefined,
        hireDate: value.hireDate ? dayjs(value.hireDate) : undefined,
      });

    // 목록이 들고 있는 값으로 먼저 그린다. 창이 비어 보이는 순간이 없도록.
    fill(target);

    /*
     * 그 다음 서버에서 상세를 받아 덮는다.
     *
     * 목록 응답에는 입사일이 연월, 생년월일이 월일로만 들어 있다(날짜 전체는
     * 암호문이다). 그래서 목록 값만으로는 그 두 칸을 **채울 수 없고**, 수정
     * 창을 열 때마다 비어 보였다. 상세 조회가 복호화한 값을 준다.
     */
    if (!target.userId) return;
    setLoadingDetail(true);
    apiClient
      .get<UserFormValue>(`/users/${target.userId}`)
      .then((detail) => fill({ ...target, ...detail }))
      .catch(() => undefined)
      .finally(() => setLoadingDetail(false));
  };

  /*
   * 역할을 두 무리로 나눠 보여 준다.
   *
   * 한 목록에 섞어 두면 '기업 관리자' 와 'ECO 일지작성자' 가 나란히 서서,
   * ECO 를 쓰게 하려고 기업 관리자를 주는 일이 생긴다. 쓰임이 다르다 —
   * 앞쪽은 이 콘솔에서 할 수 있는 일, 뒤쪽은 제품에서 할 수 있는 일이다.
   */
  const roleOptions = (() => {
    const all = roles.data ?? [];
    const label = (r: (typeof all)[number]) =>
      r.description ? `${r.roleName} — ${r.description}` : r.roleName;

    const eco = all.filter((r) => r.roleCode?.startsWith('ECO_'));
    const console = all.filter((r) => !r.roleCode?.startsWith('ECO_'));

    const groups = [
      { label: 'WORK&Hub 콘솔', options: console.map((r) => ({ value: r.roleId, label: label(r) })) },
      { label: 'WORK&ECO', options: eco.map((r) => ({ value: r.roleId, label: label(r) })) },
    ];

    // 비어 있는 무리는 빼 둔다. ECO 를 안 쓰는 기업에 빈 제목만 뜨면 어수선하다.
    return groups.filter((g) => g.options.length > 0);
  })();

  const departmentOptions = flattenDepartments(departments.data ?? []);
  const positions = codes.data?.POSITION ?? [];
  const employmentTypes = codes.data?.EMPLOYMENT_TYPE ?? EMPLOYMENT_FALLBACK;

  return (
    <Modal
      open={open}
      title={isEdit ? '구성원 정보 수정' : '구성원 등록'}
      okText={isEdit ? '저장' : '등록하고 초대 메일 발송'}
      cancelText="취소"
      confirmLoading={save.isPending || loadingDetail}
      okButtonProps={{ disabled: loadingDetail }}
      onCancel={onClose}
      onOk={() => {
        form
          .validateFields()
          .then((values) =>
            save.mutate({
              ...values,
              birthDate: values.birthDate ? dayjs(values.birthDate).format('YYYY-MM-DD') : null,
              hireDate: values.hireDate ? dayjs(values.hireDate).format('YYYY-MM-DD') : null,
            }),
          )
          .catch(() => undefined);
      }}
      destroyOnHidden
      afterOpenChange={applyInitialValues}
      width={640}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* 「아니오」로 멈춘 경우(SilentAbort)는 메시지가 비어 있어 그리지 않는다 */}
        {save.isError && errorText(save.error) && (
          <Alert type="error" showIcon message={errorText(save.error)} />
        )}

        {!isEdit && (
          <Alert
            type="info"
            showIcon
            message="비밀번호는 본인이 설정합니다"
            description="등록하면 초대 메일이 발송되고, 본인이 링크에서 비밀번호를 설정합니다. 관리자는 비밀번호를 볼 수 없습니다."
          />
        )}

        <Form form={form} layout="vertical" requiredMark={false}>
          <Section no={1} title="기본" hint="사람을 알아보고 연락하는 데 필요한 것만." />

          <Form.Item
            name="email"
            label="이메일 (로그인 ID)"
            rules={[
              { required: true, message: '이메일을 입력하세요' },
              { type: 'email', message: '올바른 이메일 형식이 아닙니다' },
            ]}
            extra={isEdit ? '등록 후에는 변경할 수 없습니다.' : undefined}
          >
            <Input disabled={isEdit} placeholder="name@company.co.kr" />
          </Form.Item>

          <Form.Item
            name="name"
            label="이름"
            rules={[{ required: true, message: '이름을 입력하세요' }, { max: 60 }]}
          >
            <Input placeholder="홍길동" />
          </Form.Item>

          <Form.Item name="phone" label="연락처">
            <Input placeholder="010-0000-0000" />
          </Form.Item>

          <Divider style={{ margin: '4px 0 16px' }} />
          <Section
            no={2}
            title="인사"
            hint="비워 두어도 등록됩니다. 나중에 채워도 됩니다."
          />

          <Space size="middle" style={{ display: 'flex' }}>
            <Form.Item name="empNo" label="사번" style={{ flex: 1 }}>
              <Input placeholder="A001" />
            </Form.Item>
            <Form.Item name="departmentId" label="부서" style={{ flex: 2 }}>
            <Select
              allowClear
              placeholder="부서 선택"
              loading={departments.isLoading}
              options={departmentOptions}
            />
            </Form.Item>
          </Space>

          <Space size="middle" style={{ display: 'flex' }}>
            <Form.Item name="positionCode" label="직위" style={{ flex: 1 }}>
              <Select
                allowClear
                placeholder="직위 선택"
                loading={codes.isLoading}
                options={positions.map((p) => ({ value: p.itemCode, label: p.itemName }))}
              />
            </Form.Item>
            <Form.Item
              name="employmentType"
              label="고용형태"
              initialValue="REGULAR"
              style={{ flex: 1 }}
            >
              <Select
                options={employmentTypes.map((e) => ({ value: e.itemCode, label: e.itemName }))}
              />
            </Form.Item>
          </Space>

          <Space size="middle" style={{ display: 'flex' }}>
            <Form.Item
              name="hireDate"
              label="입사일"
              style={{ flex: 1 }}
              extra="목록 필터에는 연월까지만 사용됩니다."
            >
              <DatePicker style={{ width: '100%' }} placeholder="입사일 선택" />
            </Form.Item>
            <Form.Item
              name="birthDate"
              label="생년월일"
              style={{ flex: 1 }}
              extra="생일 알림에는 월일만 사용됩니다."
            >
              <DatePicker style={{ width: '100%' }} placeholder="생년월일 선택" />
            </Form.Item>
          </Space>

          <Divider style={{ margin: '4px 0 16px' }} />
          <Section
            no={3}
            title="권한"
            hint="무엇을 쓸 수 있는지. 비워 두면 로그인만 되고 콘솔 기능은 열리지 않습니다."
          />

          <Form.Item
            name="roleIds"
            label="역할"
            extra="콘솔에서 무엇을 할 수 있는지, 그리고 WORK&ECO 를 쓸 수 있는지를 정합니다. 여러 개를 함께 줄 수 있습니다(예: 일반 사용자 + ECO 일지작성자)."
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="역할 선택 (여러 개 가능)"
              loading={roles.isLoading}
              options={roleOptions}
            />
          </Form.Item>

          <Form.Item
            name={['productRoles', 'crm']}
            label="CRM 권한"
            extra="WORK&CRM 안에서 어떤 메뉴를 쓸 수 있는지 정합니다. 위의 WORK&Hub 권한과는 별개이며, 저장하면 CRM 에 반영됩니다. 비워 두면 CRM 기본값(팀원)이 적용됩니다."
          >
            <Select allowClear placeholder="CRM 권한 선택" options={CRM_ROLES} />
          </Form.Item>
        </Form>

        <Text type="secondary" style={{ fontSize: 12 }}>
          이름·이메일·연락처·생년월일은 암호화되어 저장됩니다.
        </Text>
      </Space>
    </Modal>
  );
}
